/**
 * Data models and default values for the project schedule application
 */

import { ValidationResult, WorkCategory, EffortBreakdown } from "../types";
import { Project } from "./Project";
import { Category } from "./Category";

/**
 * Default color palette for projects
 */
export const DEFAULT_COLORS: string[] = [
  "#3b82f6", // Blue
  "#8b5cf6", // Purple
  "#10b981", // Green
  "#f59e0b", // Orange
  "#ef4444", // Red
  "#06b6d4", // Cyan
  "#ec4899", // Pink
  "#8b5cf6", // Violet
  "#14b8a6", // Teal
  "#f97316", // Orange-red
];

/**
 * Default color palette for categories
 */
export const CATEGORY_COLORS: string[] = [
  "#1e40af", // Deep Blue
  "#7c3aed", // Deep Purple
  "#059669", // Deep Green
  "#d97706", // Deep Orange
  "#dc2626", // Deep Red
  "#0891b2", // Deep Cyan
];

/**
 * Create a new project with default values
 * @param overrides - Properties to override defaults
 * @returns New Project instance
 */
export const createDefaultProject = (
  overrides: Partial<{
    id: number | null;
    name: string;
    startDate: Date;
    endDate: Date;
    color: string;
    effortBreakdown: EffortBreakdown;
  }> = {}
): Project => {
  const today = new Date();
  const thirtyDaysLater = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  const defaultEffortBreakdown = {
    [WorkCategory.DEVELOPMENT]: 10,
    [WorkCategory.IA]: 0,
    [WorkCategory.PROJECT_MANAGEMENT]: 0,
    [WorkCategory.CONTENT_CREATION]: 0,
    [WorkCategory.COG_SCI]: 0,
  };

  return new Project({
    id: null,
    name: "",
    startDate: today,
    endDate: thirtyDaysLater,
    color: DEFAULT_COLORS[0],
    effortBreakdown: defaultEffortBreakdown,
    ...overrides,
  });
};

/**
 * Sample categories with hierarchical structure for demo purposes
 * Categories now use level-based colors automatically
 */
export const SAMPLE_CATEGORIES: Category[] = [
  new Category({
    id: 1,
    name: "Product Development",
    level: 0,
    subcategories: [
      new Category({
        id: 11,
        name: "Web Platform",
        level: 1,
        parentId: 1,
        color: "#3b82f6",
        projects: [
          new Project({
            id: 1,
            name: "Website Redesign",
            startDate: new Date(2026, 0, 15), // Jan 15, 2025
            endDate: new Date(2026, 9, 30), // Apr 30, 2025
            color: "",
            effortBreakdown: {
              [WorkCategory.DEVELOPMENT]: 15,
              [WorkCategory.IA]: 5,
              [WorkCategory.PROJECT_MANAGEMENT]: 3,
              [WorkCategory.CONTENT_CREATION]: 2,
              [WorkCategory.COG_SCI]: 5,
            },
          }),
          new Project({
            id: 11,
            name: "Ttatata",
            startDate: new Date(2026, 0, 15), // Jan 15, 2025
            endDate: new Date(2026, 9, 30), // Apr 30, 2025
            color: "#3b82f6",
            effortBreakdown: {
              [WorkCategory.DEVELOPMENT]: 15,
              [WorkCategory.IA]: 5,
              [WorkCategory.PROJECT_MANAGEMENT]: 3,
              [WorkCategory.CONTENT_CREATION]: 2,
              [WorkCategory.COG_SCI]: 5,
            },
          }),
        ],
      }),
      new Category({
        id: 12,
        name: "Mobile Apps",
        level: 1,
        parentId: 1,
        projects: [
          new Project({
            id: 2,
            name: "iOS App Development",
            startDate: new Date(2025, 1, 1), // Feb 1, 2025
            endDate: new Date(2025, 6, 15), // Jul 15, 2025
            color: "#8b5cf6",
            effortBreakdown: {
              [WorkCategory.DEVELOPMENT]: 20,
              [WorkCategory.IA]: 4,
              [WorkCategory.PROJECT_MANAGEMENT]: 4,
              [WorkCategory.CONTENT_CREATION]: 2,
              [WorkCategory.COG_SCI]: 5,
            },
          }),
        ],
      }),
    ],
  }),
  new Category({
    id: 2,
    name: "Infrastructure",
    level: 0,
    projects: [
      new Project({
        id: 3,
        name: "Infrastructure Upgrade",
        startDate: new Date(2025, 2, 10), // Mar 10, 2025
        endDate: new Date(2025, 4, 20), // May 20, 2025
        color: "#10b981",
        effortBreakdown: {
          [WorkCategory.DEVELOPMENT]: 10,
          [WorkCategory.IA]: 2,
          [WorkCategory.PROJECT_MANAGEMENT]: 3,
          [WorkCategory.CONTENT_CREATION]: 0,
          [WorkCategory.COG_SCI]: 0,
        },
      }),
      new Project({
        id: 5,
        name: "Data Migration",
        startDate: new Date(2025, 5, 1), // Jun 1, 2025
        endDate: new Date(2025, 11, 31), // Dec 31, 2025
        color: "#ef4444",
        effortBreakdown: {
          [WorkCategory.DEVELOPMENT]: 14,
          [WorkCategory.IA]: 3,
          [WorkCategory.PROJECT_MANAGEMENT]: 2,
          [WorkCategory.CONTENT_CREATION]: 1,
          [WorkCategory.COG_SCI]: 0,
        },
      }),
    ],
  }),
  new Category({
    id: 3,
    name: "Marketing",
    level: 0,
    projects: [
      new Project({
        id: 4,
        name: "Marketing Campaign",
        startDate: new Date(2025, 3, 1), // Apr 1, 2025
        endDate: new Date(2025, 8, 30), // Sep 30, 2025
        color: "#f59e0b",
        effortBreakdown: {
          [WorkCategory.DEVELOPMENT]: 2,
          [WorkCategory.IA]: 1,
          [WorkCategory.PROJECT_MANAGEMENT]: 2,
          [WorkCategory.CONTENT_CREATION]: 5,
          [WorkCategory.COG_SCI]: 0,
        },
      }),
      new Project({
        id: 6,
        name: "Social Media Strategy",
        startDate: new Date(2025, 2, 15), // Mar 15, 2025
        endDate: new Date(2025, 7, 31), // Aug 31, 2025
        color: "#ec4899",
        effortBreakdown: {
          [WorkCategory.DEVELOPMENT]: 1,
          [WorkCategory.IA]: 0,
          [WorkCategory.PROJECT_MANAGEMENT]: 1,
          [WorkCategory.CONTENT_CREATION]: 8,
          [WorkCategory.COG_SCI]: 2,
        },
      }),
      new Project({
        id: 7,
        name: "Brand Guidelines Update",
        startDate: new Date(2025, 4, 1), // May 1, 2025
        endDate: new Date(2025, 5, 30), // Jun 30, 2025
        color: "#14b8a6",
        effortBreakdown: {
          [WorkCategory.DEVELOPMENT]: 3,
          [WorkCategory.IA]: 2,
          [WorkCategory.PROJECT_MANAGEMENT]: 1,
          [WorkCategory.CONTENT_CREATION]: 6,
          [WorkCategory.COG_SCI]: 1,
        },
      }),
    ],
  }),
  new Category({
    id: 4,
    name: "Research & Development",
    level: 0,
    projects: [
      new Project({
        id: 8,
        name: "AI Integration Research",
        startDate: new Date(2025, 0, 1), // Jan 1, 2025
        endDate: new Date(2025, 9, 31), // Oct 31, 2025
        color: "#f97316",
        effortBreakdown: {
          [WorkCategory.DEVELOPMENT]: 12,
          [WorkCategory.IA]: 8,
          [WorkCategory.PROJECT_MANAGEMENT]: 2,
          [WorkCategory.CONTENT_CREATION]: 1,
          [WorkCategory.COG_SCI]: 10,
        },
      }),
      new Project({
        id: 9,
        name: "User Experience Study",
        startDate: new Date(2025, 1, 15), // Feb 15, 2025
        endDate: new Date(2025, 4, 15), // May 15, 2025
        color: "#06b6d4",
        effortBreakdown: {
          [WorkCategory.DEVELOPMENT]: 2,
          [WorkCategory.IA]: 10,
          [WorkCategory.PROJECT_MANAGEMENT]: 1,
          [WorkCategory.CONTENT_CREATION]: 3,
          [WorkCategory.COG_SCI]: 8,
        },
      }),
    ],
  }),
];

/**
 * Helper to create a default category
 */
export const createDefaultCategory = (
  overrides: Partial<{
    id: number | null;
    name: string;
    color: string;
    projects: Project[];
    subcategories: Category[];
    parentId: number | null;
    level: number;
  }> = {}
): Category => {
  return new Category({
    id: null,
    name: "",
    color: CATEGORY_COLORS[0],
    projects: [],
    subcategories: [],
    parentId: null,
    level: 0,
    ...overrides,
  });
};
